import { Builder } from '@builder.io/react';
import { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectSelectedCurrency } from '../../../../../../store/user';
import ReactSelect from 'react-select';
import Skeleton from 'react-loading-skeleton';
import CustomButton from '../../../../../../components/general/button';
import ConvertUSD from '../../../../../../utils/convertUSD';
import useHandleAddProduct from '../../../../../../utils/handleAddProduct';
import SelectCurrency from '../../../../../../components/general/selectCurrency';
import creditImg from '../../../../../../images/payment/credit-cards.png';
import ShowCurrencies from '../../../../../../utils/showCurrencies';
import { selectProducts, selectLastOrders } from '../../../../../../store/products';
import { BASKET_CACHE_KEY } from '../../../../../../utils/handleLocalStorage';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import useCart from '../../../../../../hooks/cart/useCart';
import { openSheet } from '../../../../../../store/sheetToggler';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

const AppealDetailsFundRaiserBox = props => {
  /* ----------------------------- Start variables ---------------------------- */
  const { defaultProduct, goal = 1000000, appealsProducts = [], targetDate = '', suggestedPrices = [], mobileLayoutOnDesktop = false, defaultPriceIndex = 2, addToCart = true, wrapperClass = '', enableShowmore = false, setRecurring = false, showSingle = true, showWeekly = true, showMonthly = true, isSquared = true, pulseSingle = false, pulseWeekly = false, pulseMonthly = false, mode = 'one-off' } = props;
  const selectedCurrencyStore = useSelector(selectSelectedCurrency);
  const [addProduct] = useHandleAddProduct();
  const dispatch = useDispatch();
  const { handleAddToGiving: handleAddToGivingCart } = useCart();
  const [daysLeft, setDaysLeft] = useState(0);
  const [selectedPrice, setSelectedPrice] = useState(suggestedPrices?.[defaultPriceIndex]?.price || 250);
  const [selectAmount, setSelectAmount] = useState('');
  const allOrders = useSelector(state => selectLastOrders(state));
  const _allProducts = useSelector(state => selectProducts(state));
  const [mainProduct, setMainProduct] = useState();
  const mainProductSetRef = useRef(false);
  const [totalSale, setTotalSale] = useState(0);
  const [totalSupporters, setTotalSupporters] = useState(0);
  // let basketStatesFromLocalStorage = JSON.parse(localStorage.getItem(BASKET_CACHE_KEY));
  const [basketStatesFromLocalStorage, setBasketStatesFromLocalStorage] = useState(() => {
    return JSON.parse(localStorage.getItem(BASKET_CACHE_KEY)) || { products: [] };
  });

  const [showMore, setShowMore] = useState(false);

  // Recurring modes setup
  const suggestedRecurringModes = [
    { value: 'one-off', label: 'Single', show: showSingle },
    { value: 'week', label: 'Weekly', show: showWeekly },
    { value: 'month', label: 'Monthly', show: showMonthly },
  ];
  
  const filteredRecurringModes = suggestedRecurringModes.filter(mode => mode.show);
  
  const [selectedRecurring, setSelectedRecurring] = useState(() => {
    const defaultMode = suggestedRecurringModes.find((item) => item.value === mode && item.show);
    if (defaultMode) {
      return defaultMode;
    }
    // Fallback to first available mode
    return filteredRecurringModes[0] || suggestedRecurringModes[0];
  });

  const handleHeight = () => {
    setShowMore(!showMore);
  };
const isMobile = useTailwindBreakpoint();
  /* ------------------------------ Start methods ----------------------------- */
  const handlePercentage = (raised, goal) => {
    return `${((Number(raised) / Number(goal)) * 100).toFixed(2)}%`;
  };
  /* ------------------------------- Start hooks ------------------------------ */
  useEffect(() => {
    const now = new Date();
    const target = new Date(targetDate);

    const diffInMs = target - now;
    const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));
    const safeDays = diffInDays > 0 ? diffInDays : 1;

    setDaysLeft(safeDays);
    // setShowTag(safeDays <= 10);
  }, [targetDate]);
  useEffect(() => {
    if (!_allProducts || !Object.keys(_allProducts).length) return;
    if (!allOrders) return;
    if (!defaultProduct?.creator) return; // ✅ Guard against undefined
    
    // Extract matching products
    const givingProducts = _allProducts.flatMap(item =>
      item.sub_categories.flatMap(sub =>
        sub.products.filter(product => {
          return appealsProducts.includes(product.creator);
        }),
      ),
    );

    // Set main product if found (only once)
    if (!mainProductSetRef.current) {
      const foundProduct = _allProducts
        .flatMap(item => item.sub_categories.flatMap(sub => sub.products))
        .find(product => product.creator === defaultProduct.creator);
      
      if (foundProduct) {
        setMainProduct(foundProduct);
        mainProductSetRef.current = true;
      }
    }
    
    // Removed selectedGivingProducts logic as it was not being used effectively
    // Normalize orders array
    const ordersArray = Array.isArray(allOrders) ? allOrders : allOrders?.data || [];
    if (ordersArray.length) {
      const updatedComingProducts = [...appealsProducts, defaultProduct.creator];
      // Filter orders for relevant creators
      const filteredOrders = ordersArray.filter(order => updatedComingProducts.includes(order.creator));
      // Calculate sums
      const saleSum = filteredOrders.reduce((sum, order) => sum + (order.total_sale || 0), 0);
      const supportersSum = filteredOrders.reduce((sum, order) => sum + (order.total_supporters || 0), 0);
      // Update state
      setTotalSale(saleSum);
      setTotalSupporters(supportersSum);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allOrders, _allProducts, appealsProducts, defaultProduct?.creator, basketStatesFromLocalStorage]);
  useEffect(() => {
    let handleBasketUpdated = event => {
      if (event.detail?.key === BASKET_CACHE_KEY) {
        const basket = JSON.parse(localStorage.getItem(BASKET_CACHE_KEY));
        setBasketStatesFromLocalStorage(basket);
      }
    };

    window.addEventListener('basketUpdated', handleBasketUpdated);
    return () => window.removeEventListener('basketUpdated', handleBasketUpdated);
  }, []);
  /* -------------------------------------------------------------------------- */

  return (
    <section className={`w-full pt-4 md:pt-8 ${wrapperClass}`}>
      {/* Image Section */}
      <div className="overflow-hidden img-hover-zoom w-full mb-4">
        {mainProduct ? (
          mainProduct.image_link ? (
            <LazyLoadImage
              alt={mainProduct.alt || mainProduct.name}
              effect="blur"
              width="100%"
              delayTime={500}
              className="cursor-pointer block"
              onError={({ currentTarget }) => {
                currentTarget.onerror = null;
                currentTarget.src = '/images/general/logo-replace.png';
              }}
              src={mainProduct.image_link}
            />
          ) : (
            <img className="w-full" src="/images/general/logo-replace.png" alt="Placeholder" />
          )
        ) : (
          <Skeleton height={200} width="100%" />
        )}
      </div>

      <h3 className={`w-full text-DarkerBlue text-2xl font-medium my-3 md:my-2`}>
        {mainProduct ? defaultProduct.title || mainProduct.name : <Skeleton height={56} />}
      </h3>
      <div className="relative">
        {mainProduct ? (
          enableShowmore ? (
            <>
              <h4 className={`text-stone-500 overflow-hidden transition-all duration-300 ${showMore ? 'h-auto mb-4' : 'max-h-[65px] md:max-h-[78px] mb-0'}`}>
                {defaultProduct.description || mainProduct.description}
              </h4>
              <ChevronDownIcon
                onClick={handleHeight}
                className={`w-6 h-6 text-[#00a3da] transition-all duration-300 cursor-pointer mx-auto ${showMore ? 'transform rotate-180' : ''}`}
              />
            </>
          ) : (
            <h4 className="text-stone-500">{defaultProduct.description || mainProduct.description}</h4>
          )
        ) : (
          <Skeleton count={4} />
        )}
      </div>
      <div className={`w-full flex-col flex justify-center pt-2 md:pt-4`}>
        <div className="w-full text-lg md:text-2xl text-DarkerBlue font-bold flex items-end mb-2">
          <div className="text-DarkerBlue font-bold [&>div]:flex [&>div]:items-end [&>div>small]:mb-1">
            {totalSale && ShowCurrencies(selectedCurrencyStore, Math.round(totalSale), true)}
          </div>
          <span className="text-sm inline-block lg:w-auto text-stone-500 mx-1 font-medium mb-1">Raised of </span>
          <div className="text-DarkerBlue font-bold mr-1 [&>div]:flex [&>div]:items-end [&>span]:mb-1">
            {ShowCurrencies(selectedCurrencyStore, Math.round(ConvertUSD(selectedCurrencyStore, goal, false)), false)}
          </div>
          <span className="text-sm inline-block lg:w-auto font-medium mb-1">goal</span>
        </div>
        <div className="flex w-full h-2 bg-[#E3E3E3] mb-2">
          <div
            className={`bg-[#F60362]`}
            style={{
              width: `${handlePercentage(totalSale, ConvertUSD(selectedCurrencyStore, goal, false))}`,
            }}
          ></div>
        </div>
        <div className="flex w-full text-xs">
          <div className="text-DarkerBlue font-bold mr-0.5">{totalSupporters}</div>
          <span className="inline-block lg:w-auto mr-1">Supporters,</span>
          <div className="text-DarkerBlue font-bold mr-0.5">{daysLeft}</div>
          <span className="inline-block lg:w-auto">days left</span>
        </div>
      </div>
      
      {/* Recurring modes (Monthly / Single) */}
      {setRecurring && filteredRecurringModes.length > 0 && (
        <div className="w-full flex gap-2 justify-center my-4 ">
          {filteredRecurringModes.map((modeOption, index) => {
            // Determine if this button should pulse
            const shouldPulse = 
              (modeOption.value === 'one-off' && pulseSingle) ||
              (modeOption.value === 'week' && pulseWeekly) ||
              (modeOption.value === 'month' && pulseMonthly);
            
            return (
              <button
                key={index}
                className={`flex-1 text-center h-12 font-semibold text-xl transition-all duration-200 border border-[#cccac5] ${
                  selectedRecurring.value === modeOption.value ? 'bg-[#F60362] !text-white' : 'bg-white text-[#F60362]'
                } ${shouldPulse ? 'blob red-white' : ''} ${isSquared ? '' : 'rounded-md'}`}
                onClick={() => setSelectedRecurring(modeOption)}
              >
                {modeOption.label}
              </button>
            );
          })}
        </div>
      )}
      
      {mobileLayoutOnDesktop ? (
        // Mobile Layout (Stacked)
        <div className="w-full flex flex-col gap-3 my-2 md:my-4 ">
          {/* Suggested Prices */}
          {suggestedPrices && suggestedPrices.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {suggestedPrices.map((item, index) => {
                return (
                  <div
                    key={`prices${index}`}
                    onClick={() => {
                      setSelectedPrice(item.price);
                      selectAmount && setSelectAmount(item.price);
                    }}
                    className={`col-span-1 cursor-pointer transition-all duration-300 border border-LightBlue ${isSquared ? '' : 'rounded-md'} flex justify-center items-center py-2 h-[42px] ${
                      selectedPrice === item.price ? 'bg-LightBlue text-white' : 'bg-white text-LightBlue'
                    }`}
                  >
                    {ShowCurrencies(selectedCurrencyStore, item.price, true)}
                  </div>
                );
              })}
            </div>
          )}
          
          {/* Currency and Input */}
          <div className="flex gap-2">
            <div className="flex-[0_0_calc(33%-8px)]">
              <SelectCurrency
                onChangeCurrency={e => {}}
                classNamePrefix="orphan_select- orphan_select--ghaza"
                className={`!w-full !text-sm max-h-[42px]- [&>div]:h-[42px]- ${isSquared ? '[&>div]:!rounded-none' : ''}`}
                openUp={isMobile}
              />
            </div>
            <div className={`flex-1 relative h-[42px] flex border border-[#ccc] focus:ring-transparent focus:border-LightBlue ${isSquared ? '' : 'rounded-[.375rem]'}`}>
              <input
                value={selectAmount}
                name="amount"
                className={`flex-1 text-[#777] w-full truncate p-2 text-sm focus:outline-none focus:ring-1 focus:ring-transparent !border-none ${isSquared ? '' : 'rounded-[.375rem]'}`}
                type="number"
                min={'0'}
                onChange={e => {
                  const value = e.target.value;
                  const regex = /^(?:[1-9]\d*)?$/;
                  if (regex.test(value)) {
                    setSelectAmount(value);
                    setSelectedPrice(value ? parseInt(value, 10) : suggestedPrices?.[defaultPriceIndex]?.price || 250);
                  }
                }}
                placeholder="Other Amount"
              />
            </div>
          </div>
          
          {/* Button */}
          <CustomButton
            onClick={() => {
              if (addToCart) {
                let tempProduct = mainProduct;
                let customAmount = Number(selectAmount) ? Number(selectAmount) : Number(selectedPrice) ? Number(selectedPrice) : 250;
                tempProduct = Object.assign({}, tempProduct, {
                  quantity: customAmount,
                  recurring: selectedRecurring.value,
                });
                handleAddToGivingCart(tempProduct, 'custom');
                dispatch(openSheet());
              } else {
                addProduct({
                  product: mainProduct,
                  currency: selectedCurrencyStore,
                  defaultPrice: selectedPrice,
                });
              }
            }}
            title={'Donate Now'}
            disabled={!mainProduct}
            type="button"
            className={`w-full !h-[42px] ${isSquared ? '!rounded-none' : 'rounded-md'}`}
          />
        </div>
      ) : (
        // Desktop Layout (Grid)
        <div className={`w-full grid grid-cols-3 gap-2 md:gap-3 my-2 md:my-4 `}>
          {suggestedPrices && suggestedPrices.length > 0 &&
            suggestedPrices.map((item, index) => {
              return (
                <div
                  key={`prices${index}`}
                  onClick={() => {
                    setSelectedPrice(item.price);
                    selectAmount && setSelectAmount(item.price);
                  }}
                  className={`col-span-1w-full cursor-pointer transition-all duration-300 border border-LightBlue ${isSquared ? '' : 'rounded-md'} flex justify-center items-center py-2 h-[42px] ${
                    selectedPrice === item.price ? 'bg-LightBlue text-white' : 'bg-white text-LightBlue'
                  }`}
                >
                  {ShowCurrencies(selectedCurrencyStore, item.price, true)}
                </div>
              );
            })}
          <>
            <SelectCurrency
              onChangeCurrency={e => {}}
              classNamePrefix="orphan_select- orphan_select--ghaza"
              className={`!w-full !text-sm max-h-[42px]- [&>div]:h-[42px]- ${isSquared ? '[&>div]:!rounded-none' : ''}`}
            />
            <div
              className={`col-span-2 sm:col-span-1 md:col-span-2 lg:col-span-1 relative h-[42px] flex border border-[#ccc] focus:ring-transparent focus:border-LightBlue ${isSquared ? '' : 'rounded-[.375rem]'} w-full`}
            >
              <input
                value={selectAmount}
                name="amount"
                className={`flex-1 text-[#777] w-full truncate p-2 text-sm focus:outline-none focus:ring-1 focus:ring-transparent !border-none ${isSquared ? '' : 'rounded-[.375rem]'}`}
                type="number"
                min={'0'}
                onChange={e => {
                  const value = e.target.value;
                  const regex = /^(?:[1-9]\d*)?$/;
                  if (regex.test(value)) {
                    setSelectAmount(value);
                    setSelectedPrice(value ? parseInt(value, 10) : suggestedPrices?.[defaultPriceIndex]?.price || 250);
                  }
                }}
                placeholder="Other Amount"
              />
              {suggestedPrices && suggestedPrices.length > 0 && (
                <div className="absolute right-0 top-0 h-[42px]">
                  <ReactSelect
                    defaultValue={{ value: selectAmount, label: selectAmount }}
                    onChange={selectedOption => {
                      setSelectAmount(selectedOption.value);
                      setSelectedPrice(selectedOption.value);
                    }}
                    classNamePrefix="price-select"
                    options={suggestedPrices.map(item => ({
                      value: item.price,
                      label: item.price,
                    }))}
                    isSearchable={false}
                    placeholder=""
                    isClearable={false}
                    className={`notranslate max-h-[42px] [&>*]:!cursor-pointer !w-full ${isSquared ? '[&>div]:!rounded-none' : '[&>div]:!rounded-md'} !max-w-6`}
                  />
                </div>
              )}
            </div>
            <CustomButton
              onClick={() => {
                if (addToCart) {
                  let tempProduct = mainProduct;
                  let customAmount = Number(selectAmount) ? Number(selectAmount) : Number(selectedPrice) ? Number(selectedPrice) : 250;
                  tempProduct = Object.assign({}, tempProduct, {
                    quantity: customAmount,
                    recurring: selectedRecurring.value,
                  });
                  handleAddToGivingCart(tempProduct, 'custom');
                  dispatch(openSheet());
                } else {
                  addProduct({
                    product: mainProduct,
                    currency: selectedCurrencyStore,
                    defaultPrice: selectedPrice,
                  });
                }
              }}
              title={'Donate Now'}
              disabled={!mainProduct}
              type="button"
              className={`w-full col-span-3 sm:col-span-1 md:col-span-3 lg:col-span-1 !h-[42px] ${isSquared ? '' : 'rounded-md'}`}
            />
          </>
        </div>
      )}
      <div className={`w-full ${isSquared ? '' : 'rounded-md'} px-3 h-[38px] bg-[#80ceff] flex items-center justify-center sm:hidden`}>
        <img src={creditImg} alt="MATW" className="max-w-full h-auto max-h-[38px]" />
      </div>
    </section>
  );
};
Builder.registerComponent(AppealDetailsFundRaiserBox, {
  name: 'AppealDetailsFundRaiserBox',
  inputs: [
    {
      name: 'defaultProduct',
      type: 'object',
      defaultValue: {
        creator: '',
        label: '',
        description: '',
      },
      friendlyName: 'Main Product Info',
      subFields: [
        {
          name: 'creator',
          type: 'string',
          helperText: 'Enter product creator ID',
          required: true,
          hideFromUI: false,
        },
        {
          name: 'title',
          type: 'string',
          helperText: 'Display name for the product',
          required: false,
          hideFromUI: false,
          friendlyName: 'Product Title',
        },
        {
          name: 'description',
          type: 'string',
          helperText: 'Product reference value (usually product ID)',
          required: false,
          hideFromUI: false,
          friendlyName: 'Product Description',
        },
      ],
    },
    {
      name: 'goal',
      label: 'Goal',
      type: 'number',
      defaultValue: 1000000,
    },
    {
      name: 'targetDate',
      type: 'date',
      defaultValue: '',
      friendlyName: 'Target Date',
      required: true,
    },
    {
      name: 'suggestedPrices',
      type: 'array',
      subFields: [
        {
          name: 'price',
          type: 'number',
        },
      ],
    },
    {
      name: 'appealsProducts',
      type: 'array',
      subFields: [
        {
          name: 'creator',
          type: 'string',
        },
      ],
    },
    {
      name: 'mobileLayoutOnDesktop',
      type: 'boolean',
      defaultValue: false,
      helperText: 'Use mobile layout (stacked) for currency, input, and button on desktop',
      friendlyName: 'Mobile Layout on Desktop'
    },
    {
      name: 'defaultPriceIndex',
      type: 'number',
      defaultValue: 2,
      helperText: 'Index of suggested price to use as default (0 = first, 1 = second, 2 = third, etc.)',
      friendlyName: 'Default Price Index'
    },
    {
      name: 'addToCart',
      type: 'boolean',
      defaultValue: true,
      helperText: 'Add to cart with side sheet (true) or direct checkout (false)',
      friendlyName: 'Enable Side Cart Add'
    },
    {
      name: 'enableShowmore',
      type: 'boolean',
      defaultValue: false,
      helperText: 'Enable show more for description',
      friendlyName: 'Enable Show More'
    },
    {
      name: 'setRecurring',
      type: 'boolean',
      defaultValue: false,
      helperText: 'Enable recurring donation modes (Single, Weekly, Monthly)',
      friendlyName: 'Enable Recurring Modes'
    },
    {
      name: 'showSingle',
      type: 'boolean',
      defaultValue: true,
      helperText: 'Show Single donation option',
      friendlyName: 'Show Single'
    },
    {
      name: 'showWeekly',
      type: 'boolean',
      defaultValue: true,
      helperText: 'Show Weekly recurring option',
      friendlyName: 'Show Weekly'
    },
    {
      name: 'showMonthly',
      type: 'boolean',
      defaultValue: true,
      helperText: 'Show Monthly recurring option',
      friendlyName: 'Show Monthly'
    },
    {
      name: 'isSquared',
      type: 'boolean',
      defaultValue: true,
      helperText: 'Use squared corners for buttons (false for rounded)',
      friendlyName: 'Squared Buttons'
    },
    {
      name: 'wrapperClass',
      type: 'string',
      defaultValue: '',
      helperText: 'Additional CSS classes for the main wrapper section',
      friendlyName: 'Wrapper CSS Classes'
    },
    {
      name: 'pulseSingle',
      type: 'boolean',
      defaultValue: false,
      helperText: 'Enable pulsing animation for Single donation button',
      friendlyName: 'Pulse Single Button'
    },
    {
      name: 'pulseWeekly',
      type: 'boolean',
      defaultValue: false,
      helperText: 'Enable pulsing animation for Weekly donation button',
      friendlyName: 'Pulse Weekly Button'
    },
    {
      name: 'pulseMonthly',
      type: 'boolean',
      defaultValue: false,
      helperText: 'Enable pulsing animation for Monthly donation button',
      friendlyName: 'Pulse Monthly Button'
    },
    {
      name: 'mode',
      type: 'enum',
      friendlyName: 'Default Recurring Mode',
      helperText: 'Select which recurring mode should be selected by default',
      enum: ['one-off', 'week', 'month'],
      enumLabels: ['Single', 'Weekly', 'Monthly'],
      defaultValue: 'one-off',
    },
  ],
});

function useTailwindBreakpoint(query = "(max-width: 768px)") {
  const [matches, setMatches] = useState(window.matchMedia(query).matches);

  useEffect(() => {
    const media = window.matchMedia(query);
    const listener = () => setMatches(media.matches);
    media.addEventListener("change", listener);
    return () => media.removeEventListener("change", listener);
  }, [query]);

  return matches;
}

export default AppealDetailsFundRaiserBox;
